import * as React from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
  AlertTriangle,
  Trash2,
  Undo2,
  Check,
  ClipboardList,
  Download,
  FileSearch,
  Flag,
  Lock,
  Pencil,
  PackagePlus,
  Plus,
  RefreshCcw,
  RefreshCw,
  X,
  Search,
} from 'lucide-react';
import { useSessao } from '@/hooks/useSessao';
import {
  useAtualizarInventario,
  useCongelarEscopo,
  useReinserirProdutos,
  useConsultasInventario,
  useDesfazerEtapa,
  useEnderecosInventario,
  useGerarCiclico,
  type CriterioCiclico,
  type SelecaoCiclica,
  useFecharEtapa,
  useInventario,
  useItensEscopo,
  usePreviaFechamento,
  usePreviaIntegracao,
  type ConflitoEndereco,
  type EscopoInventario,
  type EtapaEscopo,
  type EtapaDesfazer,
  type EtapaFechamento,
  type CodigoSemCadastro,
  type ItemDivergente,
  type ItemEmRisco,
  type PreviaFechamento,
  type ItemEscopo,
} from '@/hooks/inventario';
import { ErroHttp } from '@/lib/api';
import { cn, dataCurta, dataHora, moeda, numero, plural } from '@/lib/utils';
import { baixarCsv, carimboArquivo, type ColunaCsv } from '@/lib/csv';
import { Botao } from '@/componentes/ui/botao';
import { CabecalhoPagina, Campo, Cartao, Esqueleto, Kpi, Pilula } from '@/componentes/ui/primitivos';
import { EstadoErro, EstadoVazio } from '@/componentes/ui/estados';
import { Gaveta } from '@/componentes/ui/gaveta';
import { NavInventario } from '@/componentes/inventario/NavInventario';
import { PainelEquipe } from '@/componentes/inventario/PainelEquipe';
import { Executando } from '@/componentes/inventario/Executando';
import { ImprimirEscopo } from '@/componentes/inventario/PapelInventario';
import { PainelContabilizacao } from '@/componentes/inventario/PainelContabilizacao';
import { HistoricoInventario } from '@/componentes/inventario/HistoricoInventario';
import { COR_TOM, percentualBipado as percentual } from '@/componentes/inventario/semaforo';
import { SeloTipo } from '@/componentes/inventario/SeloTipo';
import { GeradorCiclico } from '@/componentes/inventario/GeradorCiclico';

/**
 * A página de um inventário: as etapas lado a lado, cada escopo na coluna da
 * etapa em que está.
 *
 * O PBS tem uma tela por etapa e nenhuma que mostre o conjunto, e o mesmo
 * inventário anda em ritmos diferentes — um escopo pode estar na recontagem
 * enquanto outro, criado ontem para cobrir o que faltou, ainda está sendo
 * montado. As colunas mostram exatamente isso; o topo soma tudo.
 *
 * Montar escopo já funciona (botão "Novo escopo"). As outras ações de cada
 * etapa (congelar, fechar, integrar) entram nas próximas entregas e aparecem
 * aqui já no lugar onde vão morar, desligadas, para a tela não mudar de forma
 * quando elas chegarem.
 */

interface ColunaEtapa {
  chave: string;
  titulo: string;
  etapas: EtapaEscopo[];
  /** As telas do PBS que viram ações desta coluna. */
  acoes: string[];
}

const COLUNAS: ColunaEtapa[] = [
  {
    chave: 'planejamento',
    titulo: 'Planejamento',
    etapas: ['montagem'],
    acoes: [],
  },
  { chave: 'contagem', titulo: 'Contagem', etapas: ['contagem'], acoes: [] },
  {
    chave: 'recontagem',
    titulo: 'Recontagem',
    etapas: ['recontagem'],
    acoes: [],
  },
  {
    chave: 'final',
    titulo: 'Contagem final',
    etapas: ['final'],
    acoes: [],
  },
  {
    chave: 'integracao',
    titulo: 'Integração',
    etapas: ['integracao', 'integrado'],
    acoes: [],
  },
];

const ROTULO_ETAPA: Record<EtapaEscopo, string> = {
  montagem: 'Montando',
  contagem: 'Em contagem',
  recontagem: 'Em recontagem',
  final: 'Em contagem final',
  integracao: 'Aguardando integração',
  integrado: 'Integrado',
};

/**
 * O que falta bipar na etapa atual do escopo.
 *
 * Na recontagem e na final o alvo não é o escopo inteiro: o coletor só
 * libera os endereços que divergiram na contagem anterior
 * (`Usp_Validar_Localizador_Inventario`). Por isso o denominador é a
 * divergência, não o total de endereços.
 */
function andamento(e: EscopoInventario): { feitos: number; alvo: number; rotulo: string } | null {
  switch (e.etapa) {
    case 'contagem':
      // Alvo = endereços com saldo na foto; vazio não precisa de bipe.
      return e.comSaldo === null
        ? { feitos: e.coletados[0], alvo: e.localizadores, rotulo: '1ª contagem' }
        : { feitos: e.bipadosComSaldo, alvo: e.comSaldo, rotulo: '1ª contagem · com saldo' };
    case 'recontagem':
      return { feitos: e.coletados[1], alvo: e.divergentes, rotulo: 'recontagem' };
    case 'final':
      return { feitos: e.coletados[2], alvo: e.divergentes, rotulo: 'contagem final' };
    default:
      return null;
  }
}

function resumoRuas(ruas: string[]): string {
  if (ruas.length === 0) return 'sem endereço';
  if (ruas.length <= 3) return ruas.join(', ');
  return `${ruas[0]} … ${ruas[ruas.length - 1]} (${ruas.length} ruas)`;
}

export function Inventario(): JSX.Element {
  const { id: bruto } = useParams();
  const id = Number(bruto);
  const valido = Number.isInteger(id) && id > 0;
  const consulta = useInventario(valido ? id : null);
  const { temPermissao } = useSessao();
  const podePlanejar = temPermissao('inventario.planejar');

  const [escopoAberto, definirEscopoAberto] = React.useState<number | null>(null);
  const [busca, definirBusca] = React.useState('');
  const [parametros] = useSearchParams();
  const recemCriado = Number(parametros.get('novo')) || null;
  const avisoCiclico = parametros.get('ciclico');
  const [gerando, definirGerando] = React.useState(avisoCiclico === 'erro');
  const [reinserindo, definirReinserindo] = React.useState(false);

  if (!valido) {
    return <EstadoVazio titulo="Inventário inválido" detalhe="O endereço da página não tem um número de inventário." />;
  }
  if (consulta.isLoading) {
    return (
      <div className="space-y-3 p-3 sm:p-5 sm:pl-8">
        <Esqueleto className="h-16 w-full" />
        <div className="grid gap-3 md:grid-cols-4">
          {[0, 1, 2, 3].map((i) => (
            <Esqueleto key={i} className="h-28" />
          ))}
        </div>
        <Esqueleto className="h-72 w-full" />
      </div>
    );
  }
  if (consulta.isError || !consulta.data) {
    return <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />;
  }

  const { inventario, escopos, conflitos } = consulta.data;
  const enderecos = escopos.reduce((s, e) => s + e.localizadores, 0);
  const alvo1 = escopos.reduce((s, e) => s + (e.comSaldo ?? 0), 0);
  const bipados = escopos.reduce((s, e) => s + (e.comSaldo === null ? 0 : e.bipadosComSaldo), 0);
  const sobras = escopos.reduce((s, e) => s + e.sobras, 0);
  const divergentes = escopos
    .filter((e) => e.etapa !== 'montagem' && e.etapa !== 'contagem')
    .reduce((s, e) => s + e.divergentes, 0);
  const integrados = escopos.filter((e) => e.etapa === 'integrado').length;
  const ultimaColeta = escopos
    .map((e) => e.ultimaColeta)
    .filter((d): d is string => d !== null)
    .sort()
    .at(-1);
  const selecionado = escopos.find((e) => e.escopo === escopoAberto) ?? null;
  const termo = busca.trim().toUpperCase();
  const visiveis = termo
    ? escopos.filter(
        (e) => String(e.escopo).includes(termo) || e.ruas.some((r) => r.toUpperCase().includes(termo)),
      )
    : escopos;

  return (
    <div className="space-y-4 p-3 sm:p-5 sm:pl-8">
      <NavInventario
        inventario={inventario.id}
        nome={inventario.nome}
        escopo={escopoAberto}
        podePlanejar={podePlanejar}
      />

      <CabecalhoPagina
        sobrelinha={`Inventário #${inventario.id} · centro ${inventario.centro}`}
        titulo={<NomeEditavel id={inventario.id} nome={inventario.nome} editavel={podePlanejar} />}
        descricao={
          <>
            <SeloTipo tipo={inventario.tipo} criterio={inventario.criterio} className="mr-1.5 align-middle" />
            Criado em {dataHora(inventario.criadoEm)}
            {inventario.criadoPor ? ` por ${inventario.criadoPor}` : ''}
            {ultimaColeta ? ` · último envio do coletor ${dataHora(ultimaColeta)}` : ''}
            {inventario.situacao === 'ENCERRADO' ? ' · encerrado' : ''}
          </>
        }
        icone={<ClipboardList className="h-5 w-5" />}
        acoes={
          <>
            <Botao
              variante="fantasma"
              tamanho="pequeno"
              onClick={() => void consulta.refetch()}
              carregando={consulta.isFetching}
            >
              <RefreshCw className="h-4 w-4" />
              Atualizar
            </Botao>
            {podePlanejar ? (
              <>
                <EncerrarReabrir id={inventario.id} situacao={inventario.situacao} />
                {inventario.situacao === 'ABERTO' && inventario.tipo === 'CICLICO' ? (
                  <Botao variante="primaria" tamanho="pequeno" onClick={() => definirGerando((a) => !a)}>
                    <RefreshCcw className="h-4 w-4" />
                    Gerar escopos
                  </Botao>
                ) : null}
                {inventario.situacao === 'ABERTO' && escopos.some((e) => ETAPAS_REINSERIR.includes(e.etapa)) ? (
                  <Botao variante="secundaria" tamanho="pequeno" onClick={() => definirReinserindo((a) => !a)}>
                    <PackagePlus className="h-4 w-4" />
                    Reinserir produtos
                  </Botao>
                ) : null}
                {inventario.situacao === 'ABERTO' ? (
                  <Botao
                    variante={inventario.tipo === 'CICLICO' ? 'secundaria' : 'primaria'}
                    tamanho="pequeno"
                    comoFilho
                  >
                    <Link to={`/inventario/${inventario.id}/escopo/novo`}>
                      <Plus className="h-4 w-4" />
                      {inventario.tipo === 'CICLICO' ? 'Montar à mão' : 'Novo escopo'}
                    </Link>
                  </Botao>
                ) : null}
              </>
            ) : null}
          </>
        }
      />

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Kpi
          rotulo="Endereços no inventário"
          valor={numero(enderecos)}
          nota={`${numero(escopos.length)} ${plural(escopos.length, 'escopo', 'escopos')}`}
          tom="neutro"
        />
        <Kpi
          rotulo="Bipados na 1ª contagem"
          valor={alvo1 > 0 ? `${Math.round((bipados / alvo1) * 100)}%` : '—'}
          nota={
            alvo1 > 0
              ? `${numero(bipados)} de ${numero(alvo1)} endereços com saldo${sobras > 0 ? ` · ${numero(sobras)} sobras` : ''}`
              : 'congele um escopo para começar'
          }
          barra={alvo1 > 0 ? (bipados / alvo1) * 100 : 0}
        />
        <Kpi
          rotulo="Endereços com divergência"
          valor={numero(divergentes)}
          nota="no último fechamento de cada escopo"
          tom={divergentes > 0 ? 'alerta' : 'neutro'}
        />
        <Kpi
          rotulo="Escopos integrados"
          valor={`${numero(integrados)} / ${numero(escopos.length)}`}
          barra={escopos.length > 0 ? (integrados / escopos.length) * 100 : 0}
          tom={escopos.length > 0 && integrados === escopos.length ? 'ok' : 'marca'}
        />
      </div>

      {recemCriado && escopos.some((e) => e.escopo === recemCriado) ? (
        <div className="flex items-center gap-3 rounded-xl border border-status-livre/30 bg-status-livreFundo px-4 py-3">
          <Check className="h-5 w-5 text-status-livre" />
          <p className="text-sm">
            <b>Escopo {recemCriado} criado no ERP</b> e já faz parte deste inventário. O próximo
            passo dele é congelar o estoque.
          </p>
        </div>
      ) : null}

      {avisoCiclico === 'pulados' || avisoCiclico === 'erro' ? (
        <div className="flex items-center gap-3 rounded-xl border border-status-atencao/40 bg-status-atencaoFundo px-4 py-3 text-sm">
          <AlertTriangle className="h-5 w-5 shrink-0 text-status-atencao" />
          <p>
            {avisoCiclico === 'erro'
              ? 'O inventário foi criado, mas os escopos não foram gerados. Use "Gerar escopos" para tentar de novo.'
              : 'Parte das ruas ou SKUs não virou escopo (sem endereço ativo no centro). Os demais foram gerados.'}
          </p>
        </div>
      ) : null}

      {gerando && inventario.tipo === 'CICLICO' && inventario.criterio ? (
        <GerarMaisEscopos
          inventario={inventario.id}
          centro={inventario.centro}
          criterio={inventario.criterio}
          aoFechar={() => definirGerando(false)}
        />
      ) : null}

      {reinserindo ? (
        <ReinserirProdutos
          inventario={inventario.id}
          escopos={escopos}
          aoFechar={() => definirReinserindo(false)}
        />
      ) : null}

      {conflitos.length > 0 ? <AvisoConflitos conflitos={conflitos} inventario={inventario.id} /> : null}

      <PainelEquipe inventario={inventario.id} />

      {escopos.length === 0 ? (
        <EstadoVazio
          titulo="Este inventário ainda não tem escopo"
          detalhe={
            podePlanejar
              ? 'Monte o primeiro escopo marcando as ruas no mapa, filtrando ou colando uma lista de endereços. Dá para criar outros a qualquer momento, mesmo com a contagem em andamento.'
              : 'Quem planeja o inventário ainda não incluiu nenhum escopo.'
          }
          acao={
            podePlanejar && inventario.situacao === 'ABERTO' ? (
              <Botao variante="primaria" comoFilho>
                <Link to={`/inventario/${inventario.id}/escopo/novo`}>
                  <Plus className="h-4 w-4" />
                  Montar escopo
                </Link>
              </Botao>
            ) : undefined
          }
        />
      ) : (
        <div className="space-y-3">
        <BuscaEscopo
          valor={busca}
          aoMudar={definirBusca}
          total={escopos.length}
          achados={visiveis.length}
          aoAbrir={() => {
            const exato = escopos.find((e) => String(e.escopo) === termo);
            const unico = visiveis.length === 1 ? visiveis[0] : undefined;
            const alvo = exato ?? unico;
            if (alvo) definirEscopoAberto(alvo.escopo);
          }}
        />
        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5">
          {COLUNAS.map((coluna) => (
            <ColunaDeEtapa
              key={coluna.chave}
              coluna={coluna}
              escopos={visiveis.filter((e) => coluna.etapas.includes(e.etapa))}
              aoAbrir={definirEscopoAberto}
              destaque={recemCriado}
            />
          ))}
        </div>
        </div>
      )}

      <HistoricoInventario inventario={inventario.id} />

      <Gaveta
        aberta={selecionado !== null}
        aoFechar={() => definirEscopoAberto(null)}
        titulo={selecionado ? `Escopo ${selecionado.escopo}` : ''}
        subtitulo={selecionado ? ROTULO_ETAPA[selecionado.etapa] : undefined}
        className="sm:w-[min(760px,100vw)]"
      >
        {selecionado ? (
          <DetalheEscopo escopo={selecionado} inventario={inventario.id} podePlanejar={podePlanejar} />
        ) : null}
      </Gaveta>
    </div>
  );
}

function ColunaDeEtapa({
  coluna,
  escopos,
  aoAbrir,
  destaque,
}: {
  coluna: ColunaEtapa;
  escopos: EscopoInventario[];
  aoAbrir: (escopo: number) => void;
  destaque: number | null;
}): JSX.Element {
  return (
    <Cartao className="flex flex-col overflow-hidden">
      <div className="border-b border-borda bg-superficie2 px-4 py-3">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-[14px] font-semibold">{coluna.titulo}</h2>
          <span className="tabular rounded-full bg-superficie3 px-2 py-0.5 text-2xs font-bold text-textoFraco">
            {escopos.length}
          </span>
        </div>
        <div className="mt-2 flex flex-wrap gap-1">
          {coluna.acoes.map((a) => (
            <span
              key={a}
              title="Chega nas próximas entregas"
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-borda px-1.5 py-0.5 text-2xs text-textoSuave"
            >
              <Lock className="h-3 w-3" />
              {a}
            </span>
          ))}
        </div>
      </div>
      <div className="flex-1 space-y-2 p-2.5">
        {escopos.length === 0 ? (
          <p className="px-1.5 py-4 text-center text-xs text-textoSuave">Nenhum escopo nesta etapa</p>
        ) : (
          escopos.map((e) => (
            <CartaoEscopo
              key={e.escopo}
              escopo={e}
              aoAbrir={() => aoAbrir(e.escopo)}
              destaque={e.escopo === destaque}
            />
          ))
        )}
      </div>
    </Cartao>
  );
}

/**
 * Desde quando o escopo está na etapa atual, e a mediana histórica dela
 * (escopos grandes integrados, 2024–2026). Parado = mais que o dobro.
 */
const DESDE_E_MEDIANA: Partial<Record<EtapaEscopo, { desde: (e: EscopoInventario) => string | null; mediana: number; aguardando: string }>> = {
  montagem: { desde: (e) => e.criadoEm, mediana: 24, aguardando: 'aguardando congelar' },
  contagem: { desde: (e) => e.congeladoEm, mediana: 3.3, aguardando: 'em contagem' },
  recontagem: { desde: (e) => e.contagemFechadaEm, mediana: 4.4, aguardando: 'em recontagem' },
  final: { desde: (e) => e.recontagemFechadaEm, mediana: 0.9, aguardando: 'em contagem final' },
  integracao: { desde: (e) => e.finalFechadoEm, mediana: 41, aguardando: 'aguardando integrar' },
};

function IdadeDaEtapa({ escopo: e }: { escopo: EscopoInventario }): JSX.Element | null {
  const regra = DESDE_E_MEDIANA[e.etapa];
  const desde = regra?.desde(e);
  if (!regra || !desde) return null;
  const h = (Date.now() - new Date(desde).getTime()) / 3_600_000;
  const parado = h > regra.mediana * 2;
  const texto = h < 1 ? `${Math.max(1, Math.round(h * 60))} min` : h < 48 ? `${Math.round(h)} h` : `${Math.round(h / 24)} dias`;
  return (
    <p
      className={cn('mt-1 text-2xs', parado ? 'font-semibold text-status-atencao' : 'text-textoSuave')}
      title={`Mediana histórica desta etapa: ${regra.mediana} h`}
    >
      {regra.aguardando} há {texto}
      {parado ? ' · acima do normal' : ''}
    </p>
  );
}

function CartaoEscopo({
  escopo: e,
  aoAbrir,
  destaque,
}: {
  escopo: EscopoInventario;
  aoAbrir: () => void;
  destaque: boolean;
}): JSX.Element {
  const a = andamento(e);
  const p = a ? percentual(a.feitos, a.alvo) : null;
  return (
    <button
      type="button"
      onClick={aoAbrir}
      className={cn(
        'block w-full rounded-lg border bg-superficie p-2.5 text-left shadow-xs transition-colors hover:border-bordaForte focus:outline-none focus-visible:ring-2 focus-visible:ring-primaria',
        destaque ? 'border-status-livre ring-2 ring-status-livre/40' : 'border-borda',
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="tabular text-[13px] font-bold">#{e.escopo}</span>
        {e.perdidas[0] + e.perdidas[1] + e.perdidas[2] > 0 ? (
          <span
            className="rounded bg-status-atencaoFundo px-1 text-2xs font-bold text-status-atencao"
            title="Coletas feitas depois do fechamento da própria contagem"
          >
            {numero(e.perdidas[0] + e.perdidas[1] + e.perdidas[2])} perdidas
          </span>
        ) : e.etapa === 'integrado' ? (
          <Check className="h-4 w-4 text-status-livre" aria-label="Integrado" />
        ) : e.etapa === 'integracao' ? (
          <Pilula tom="marca">pronto</Pilula>
        ) : null}
      </div>
      <p className="mt-0.5 truncate text-xs text-textoFraco" title={e.ruas.join(', ')}>
        {resumoRuas(e.ruas)} · {numero(e.localizadores)} end.
      </p>
      <IdadeDaEtapa escopo={e} />
      {a && p ? (
        <div className="mt-2">
          <div className="flex items-baseline justify-between gap-2 text-2xs text-textoFraco">
            <span className="truncate">{a.rotulo}</span>
            <span className="tabular shrink-0">
              <span className={cn('text-[13px] font-bold', COR_TOM[p.tom].texto)}>{p.pct}%</span>{' '}
              <span className="font-semibold text-texto">
                {numero(a.feitos)}/{numero(a.alvo)}
              </span>
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-superficie3">
            <span
              className={cn('block h-full rounded-full transition-[width] duration-500', COR_TOM[p.tom].barra)}
              style={{ width: `${Math.max(a.feitos > 0 ? 2 : 0, p.pct)}%` }}
            />
          </div>
        </div>
      ) : e.etapa === 'montagem' ? (
        <p className="mt-1.5 text-2xs font-semibold text-primaria">Abrir para congelar →</p>
      ) : e.divergentes > 0 ? (
        <p className="mt-1.5 text-2xs text-status-atencao">
          {numero(e.divergentes)} {plural(e.divergentes, 'endereço divergente', 'endereços divergentes')}
        </p>
      ) : null}
    </button>
  );
}

/**
 * O % bipado DO ESCOPO, no topo do detalhe — o cartão do inventário mostra o
 * de todos os escopos somados, e aqui cada um responde por si.
 *
 * Sempre a 1ª contagem sobre os endereços com saldo na foto (a mesma conta do
 * cartão do inventário). Na recontagem e na final vem junto o andamento da
 * etapa atual, cujo alvo são só os divergentes.
 */
function BipadoDoEscopo({ escopo: e }: { escopo: EscopoInventario }): JSX.Element | null {
  if (e.comSaldo === null) return null; // antes de congelar não há alvo
  const linhas: { rotulo: string; feitos: number; alvo: number; nota: string }[] = [
    {
      rotulo: '1ª contagem',
      feitos: e.bipadosComSaldo,
      alvo: e.comSaldo,
      nota: 'endereços com saldo bipados',
    },
  ];
  const a = andamento(e);
  if (a && (e.etapa === 'recontagem' || e.etapa === 'final')) {
    linhas.push({ rotulo: a.rotulo, feitos: a.feitos, alvo: a.alvo, nota: 'divergentes bipados' });
  }
  return (
    <div className="space-y-3 rounded-xl border border-borda bg-superficie2 p-3">
      {linhas.map((l) => {
        const p = percentual(l.feitos, l.alvo);
        return (
          <div key={l.rotulo}>
            <div className="flex items-baseline justify-between gap-2">
              <span className="rotulo">Bipado · {l.rotulo}</span>
              <span className={cn('tabular text-2xl font-bold leading-none', COR_TOM[p.tom].texto)}>
                {p.pct}%
              </span>
            </div>
            <div className="mt-1.5 h-2 overflow-hidden rounded-full bg-superficie3">
              <span
                className={cn('block h-full rounded-full transition-[width] duration-500', COR_TOM[p.tom].barra)}
                style={{ width: `${Math.max(l.feitos > 0 ? 2 : 0, p.pct)}%` }}
              />
            </div>
            <p className="tabular mt-1 text-xs text-textoFraco">
              {numero(l.feitos)} de {numero(l.alvo)} {l.nota}
              {l.rotulo === '1ª contagem' && e.sobras > 0
                ? ` · ${numero(e.sobras)} ${plural(e.sobras, 'vazio bipado', 'vazios bipados')} (fora da conta)`
                : ''}
            </p>
          </div>
        );
      })}
    </div>
  );
}

function DetalheEscopo({
  escopo: e,
  inventario,
  podePlanejar,
}: {
  escopo: EscopoInventario;
  inventario: number;
  podePlanejar: boolean;
}): JSX.Element {
  const marcos: { rotulo: string; em: string | null; extra?: string | undefined; mediana?: number | undefined }[] = [
    { rotulo: 'Escopo criado', em: e.criadoEm, extra: e.criadoPor ?? undefined },
    {
      rotulo: 'Estoque congelado',
      em: e.congeladoEm,
      extra: e.dataBase ? `data-base ${dataCurta(e.dataBase)}` : undefined,
      mediana: 24,
    },
    {
      rotulo: '1ª contagem fechada',
      em: e.contagemFechadaEm,
      extra: `${numero(e.coletados[0])} endereços bipados`,
      mediana: 3.3,
    },
    {
      rotulo: 'Recontagem fechada',
      em: e.recontagemFechadaEm,
      extra: `${numero(e.coletados[1])} endereços bipados`,
      mediana: 4.4,
    },
    {
      rotulo: 'Contagem final fechada',
      em: e.finalFechadoEm,
      extra: `${numero(e.coletados[2])} endereços bipados`,
      mediana: 0.9,
    },
    { rotulo: 'Integrado ao estoque', em: e.integradoEm, mediana: 41 },
  ];

  return (
    <div className="space-y-5 p-5">
      {podePlanejar && e.etapa === 'montagem' ? (
        <Congelar escopo={e} inventario={inventario} />
      ) : null}
      {podePlanejar && (e.etapa === 'contagem' || e.etapa === 'recontagem' || e.etapa === 'final') ? (
        <Fechar key={`fechar-${e.escopo}-${e.etapa}`} escopo={e} inventario={inventario} etapa={e.etapa} />
      ) : null}
      {podePlanejar && e.etapa === 'integracao' ? (
        <Integrar key={`integrar-${e.escopo}-${e.etapa}`} escopo={e} inventario={inventario} />
      ) : null}
      {podePlanejar ? <Desfazer key={`desfazer-${e.escopo}-${e.etapa}`} escopo={e} inventario={inventario} /> : null}
      <AvisoPerdidas escopo={e} />
      <BipadoDoEscopo escopo={e} />
      <div className="grid grid-cols-2 gap-3 text-sm">
        <div>
          <p className="rotulo">Endereços</p>
          <p className="tabular text-lg font-bold">{numero(e.localizadores)}</p>
        </div>
        <div>
          <p className="rotulo">Produtos</p>
          <p className="tabular text-lg font-bold">{numero(e.produtos)}</p>
        </div>
        <div>
          <p className="rotulo">Divergentes</p>
          <p className="tabular text-lg font-bold">{numero(e.divergentes)}</p>
        </div>
        <div className="col-span-2">
          <p className="rotulo">Ruas</p>
          <p className="text-sm">{e.ruas.length > 0 ? e.ruas.join(', ') : '—'}</p>
        </div>
        <div className="col-span-2">
          <p className="rotulo">Origem</p>
          <p className="text-sm">
            {e.origem === 'WMS' ? 'Montado no WMS' : 'Criado no PBS'}
          </p>
        </div>
      </div>

      {e.integradoEm ? <PainelContabilizacao inventario={inventario} escopo={e.escopo} /> : null}
      <ItensDoEscopo escopo={e} inventario={inventario} />
      <PainelEquipe inventario={inventario} escopo={e.escopo} compacto />
      <ConsultasDoInventario inventario={inventario} escopo={e.escopo} compacto />

      <ol className="relative space-y-3 border-l border-borda pl-4">
        {marcos.map((m) => (
          <li key={m.rotulo} className="relative">
            <span
              className={cn(
                'absolute -left-[21px] top-1 h-2.5 w-2.5 rounded-full ring-2 ring-superficie',
                m.em ? 'bg-status-livre' : 'bg-superficie3',
              )}
            />
            <p className={cn('text-sm font-medium', !m.em && 'text-textoSuave')}>{m.rotulo}</p>
            <p className="text-xs text-textoFraco">
              {m.em ? dataHora(m.em) : 'ainda não'}
              {m.em && m.extra ? ` · ${m.extra}` : ''}
            </p>
            <TempoDaEtapa marcos={marcos} indice={marcos.indexOf(m)} />
          </li>
        ))}
      </ol>

    </div>
  );
}

/**
 * Endereços em dois escopos abertos do mesmo centro.
 *
 * O PBS deixa, e a conta vem na integração: cada escopo ajusta o mesmo saldo
 * e o endereço é acertado duas vezes. O montador do WMS vai impedir; os
 * escopos que vieram do PBS podem já ter o problema, e é aqui que ele aparece
 * — antes de fechar, quando ainda dá para tirar o endereço de um deles.
 */
function AvisoConflitos({
  conflitos,
  inventario,
}: {
  conflitos: ConflitoEndereco[];
  inventario: number;
}): JSX.Element {
  // Cada par aparece duas vezes quando os dois escopos são deste inventário.
  const enderecos = new Map<number, ConflitoEndereco[]>();
  for (const c of conflitos) {
    const lista = enderecos.get(c.localizador) ?? [];
    lista.push(c);
    enderecos.set(c.localizador, lista);
  }
  return (
    <details className="rounded-xl border border-status-atencao/30 bg-status-atencaoFundo px-4 py-3.5 shadow-xs">
      <summary className="flex cursor-pointer list-none items-start gap-3">
        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-status-atencao" />
        <div>
          <p className="text-sm font-semibold">
            {numero(enderecos.size)}{' '}
            {plural(enderecos.size, 'endereço está', 'endereços estão')} em mais de um escopo aberto
          </p>
          <p className="text-xs text-textoFraco">
            Se os dois escopos forem integrados, o saldo desses endereços é ajustado duas vezes.
            Clique para ver quais.
          </p>
        </div>
      </summary>
      <div className="mt-3 max-h-64 overflow-y-auto rounded-lg border border-borda bg-superficie">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-superficie2 text-left text-textoFraco">
            <tr>
              <th className="px-3 py-1.5 font-medium">Endereço</th>
              <th className="px-3 py-1.5 font-medium">Escopo deste inventário</th>
              <th className="px-3 py-1.5 font-medium">Também no escopo</th>
            </tr>
          </thead>
          <tbody>
            {[...enderecos.entries()].map(([localizador, lista]) => {
              const escoposDaqui = [...new Set(lista.map((c) => c.escopo))];
              const outros = [...new Set(lista.map((c) => c.outroEscopo))].filter(
                (o) => !escoposDaqui.includes(o) || escoposDaqui.length > 1,
              );
              return (
                <tr key={localizador} className="border-t border-borda">
                  <td className="px-3 py-1.5">
                    <span className="font-mono">{lista[0]?.endereco ?? localizador}</span>
                    <span className="ml-1 text-textoSuave">({localizador})</span>
                  </td>
                  <td className="tabular px-3 py-1.5">{escoposDaqui.join(', ')}</td>
                  <td className="tabular px-3 py-1.5">
                    {outros
                      .map((o) => {
                        const c = lista.find((x) => x.outroEscopo === o);
                        const dono = c?.outroInventario;
                        return dono === inventario
                          ? `${o} (este inventário)`
                          : dono
                            ? `${o} (inventário #${dono})`
                            : `${o} (sem inventário)`;
                      })
                      .join(', ')}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </details>
  );
}

function NomeEditavel({
  id,
  nome,
  editavel,
}: {
  id: number;
  nome: string;
  editavel: boolean;
}): JSX.Element {
  const atualizar = useAtualizarInventario(id);
  const [editando, definirEditando] = React.useState(false);
  const [valor, definirValor] = React.useState(nome);

  if (!editando) {
    return (
      <span className="inline-flex items-center gap-2">
        {nome}
        {editavel ? (
          <button
            type="button"
            onClick={() => {
              definirValor(nome);
              definirEditando(true);
            }}
            className="rounded-md p-1 text-textoSuave hover:bg-superficie2 hover:text-texto"
            aria-label="Renomear inventário"
          >
            <Pencil className="h-4 w-4" />
          </button>
        ) : null}
      </span>
    );
  }
  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(ev) => {
        ev.preventDefault();
        if (valor.trim().length < 3) return;
        atualizar.mutate({ nome: valor.trim() }, { onSuccess: () => definirEditando(false) });
      }}
    >
      <Campo
        autoFocus
        value={valor}
        maxLength={120}
        onChange={(ev) => definirValor(ev.target.value)}
        className="min-w-[280px] text-base font-semibold"
      />
      <Botao type="submit" variante="primaria" tamanho="pequeno" carregando={atualizar.isPending}>
        Salvar
      </Botao>
      <Botao type="button" variante="fantasma" tamanho="pequeno" onClick={() => definirEditando(false)}>
        Cancelar
      </Botao>
    </form>
  );
}

function EncerrarReabrir({ id, situacao }: { id: number; situacao: 'ABERTO' | 'ENCERRADO' }): JSX.Element {
  const atualizar = useAtualizarInventario(id);
  const encerrado = situacao === 'ENCERRADO';
  return (
    <Botao
      variante="fantasma"
      tamanho="pequeno"
      carregando={atualizar.isPending}
      title={
        encerrado
          ? 'Volta para a lista de inventários em andamento.'
          : 'Tira da lista de trabalho. Não mexe em nada do ERP.'
      }
      onClick={() => atualizar.mutate({ situacao: encerrado ? 'ABERTO' : 'ENCERRADO' })}
    >
      {encerrado ? 'Reabrir' : 'Encerrar'}
    </Botao>
  );
}

function hojeLocal(): string {
  const d = new Date();
  const p = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/**
 * Congelar = tirar a foto do saldo na data-base, contra a qual a contagem vai
 * ser comparada. Sem isso o coletor recusa o escopo ("Congelamento do Escopo
 * não Realizado"). O padrão é hoje, como no PBS; a data só volta no tempo
 * quando o inventário é sobre um dia que já passou.
 */
function Congelar({ escopo: e, inventario }: { escopo: EscopoInventario; inventario: number }): JSX.Element {
  const congelar = useCongelarEscopo(inventario);
  const [dataBase, definirDataBase] = React.useState(hojeLocal());
  const [confirmando, definirConfirmando] = React.useState(false);

  return (
    <div className="rounded-xl border border-primaria/30 bg-primaria-suave p-4">
      <p className="text-sm font-semibold">Próximo passo: congelar o estoque</p>
      <p className="mt-1 text-xs text-textoFraco">
        Tira a foto do saldo dos {numero(e.produtos)} produtos nos {numero(e.localizadores)} endereços
        deste escopo, na data-base. É contra ela que a contagem é comparada, e só depois disso o
        coletor libera o escopo. Mesma regra do formulário "Congelar Estoque Inventário" do PBS.
      </p>
      <label className="mt-3 block">
        <span className="rotulo mb-1 block">Data-base</span>
        <input
          type="date"
          value={dataBase}
          onChange={(ev) => {
            definirDataBase(ev.target.value);
            definirConfirmando(false);
          }}
          className="min-h-toque w-full rounded-lg border border-borda bg-superficie px-3 text-sm"
        />
      </label>
      <Executando ativo={congelar.isPending} oQue="Congelando o estoque" />
      {congelar.isError ? (
        <p className="mt-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-xs text-status-divergencia">
          {mensagemDeErro(congelar.error, 'Não foi possível congelar.')}
        </p>
      ) : null}
      {congelar.isSuccess ? (
        <p className="mt-2 rounded-lg bg-status-livreFundo px-3 py-2 text-xs">
          Congelado: {numero(congelar.data.produtos)} produtos, {numero(congelar.data.linhas)} linhas de saldo
          na data-base {dataCurta(congelar.data.dataBase)}.
        </p>
      ) : confirmando ? (
        <div className="mt-3 space-y-2">
          <p className="text-xs">
            Grava no ERP, com a mesma regra do PBS. Dá para congelar de novo até fechar a recontagem
            — a foto anterior é substituída.
          </p>
          <div className="flex gap-2">
            <Botao
              variante="primaria"
              tamanho="pequeno"
              carregando={congelar.isPending}
              onClick={() => congelar.mutate({ escopo: e.escopo, dataBase })}
            >
              <Lock className="h-4 w-4" />
              Confirmar congelamento
            </Botao>
            <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirConfirmando(false)}>
              Voltar
            </Botao>
          </div>
        </div>
      ) : (
        <Botao
          variante="primaria"
          className="mt-3 w-full"
          disabled={!dataBase}
          onClick={() => definirConfirmando(true)}
        >
          <Lock className="h-4 w-4" />
          Congelar estoque
        </Botao>
      )}
    </div>
  );
}

const COLUNAS_ITENS_CSV: ColunaCsv<ItemEscopo>[] = [
  { cabecalho: 'Rua', valor: (i) => i.rua ?? '' },
  { cabecalho: 'Endereço', valor: (i) => i.endereco ?? '', texto: true },
  { cabecalho: 'Localizador', valor: (i) => i.localizador, texto: true },
  { cabecalho: 'SKU', valor: (i) => i.produto ?? '', texto: true },
  { cabecalho: 'Descrição', valor: (i) => i.descricao ?? '(endereço vazio)' },
  { cabecalho: 'EAN', valor: (i) => i.ean ?? '', texto: true },
  { cabecalho: 'Saldo hoje', valor: (i) => i.saldoAtual ?? '' },
  { cabecalho: 'Saldo congelado', valor: (i) => i.saldoCongelado ?? '' },
];

/**
 * O que vai ser contado, endereço por endereço.
 *
 * É a grade de produtos do formulário de escopo do PBS, aberta pelo endereço
 * — como a equipe anda no CD. Endereço vazio aparece também: ele é conferido,
 * e o que for bipado nele vira sobra.
 */
function ItensDoEscopo({ escopo: e, inventario }: { escopo: EscopoInventario; inventario: number }): JSX.Element {
  const consulta = useItensEscopo(inventario, e.escopo);
  const detalhe = useInventario(inventario);
  const enderecosInv = useEnderecosInventario(inventario);
  const [busca, definirBusca] = React.useState('');
  const itens = consulta.data?.itens ?? [];
  const congelado = e.congeladoEm !== null;

  const termo = busca.trim().toUpperCase();
  const filtrados = termo
    ? itens.filter(
        (i) =>
          (i.endereco ?? '').toUpperCase().includes(termo) ||
          (i.descricao ?? '').toUpperCase().includes(termo) ||
          (i.ean ?? '').includes(termo) ||
          String(i.produto ?? '') === termo,
      )
    : itens;

  const grupos = new Map<number, ItemEscopo[]>();
  for (const i of filtrados) {
    const l = grupos.get(i.localizador) ?? [];
    l.push(i);
    grupos.set(i.localizador, l);
  }
  const enderecos = new Set(itens.map((i) => i.localizador)).size;
  const vazios = [...new Set(itens.filter((i) => i.produto === null).map((i) => i.localizador))].length;
  const pares = itens.filter((i) => i.produto !== null).length;

  // A folha segue o alvo do coletor: na recontagem e na final, só o que
  // divergiu na etapa anterior.
  const n = e.etapa === 'recontagem' ? 1 : e.etapa === 'final' ? 2 : 0;
  const alvo =
    n === 0
      ? null
      : new Set(
          (enderecosInv.data?.enderecos ?? [])
            .filter((x) => x.escopo === e.escopo && x.divergente[n - 1])
            .map((x) => x.localizador),
        );
  const itensDaFolha = alvo ? itens.filter((i) => alvo.has(i.localizador)) : itens;
  const rotuloContagem =
    e.etapa === 'recontagem' ? '2ª (recontagem)' : e.etapa === 'final' ? '3ª (final)' : '1ª';

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm font-semibold">O que vai ser contado</p>
        <div className="ml-auto flex items-center gap-1">
        {detalhe.data ? (
          <ImprimirEscopo
            inventario={detalhe.data.inventario.nome}
            escopo={e.escopo}
            centro={detalhe.data.inventario.centro}
            contagem={rotuloContagem}
            itens={itensDaFolha}
          />
        ) : null}
        <Botao
          variante="fantasma"
          tamanho="micro"
          disabled={itens.length === 0}
          onClick={() => baixarCsv(`escopo-${e.escopo}-${carimboArquivo()}`, itens, COLUNAS_ITENS_CSV)}
        >
          <Download className="h-3.5 w-3.5" />
          Excel
        </Botao>
        </div>
      </div>
      {consulta.isLoading ? (
        <div className="space-y-1.5">
          {[0, 1, 2, 3].map((k) => (
            <Esqueleto key={k} className="h-9" />
          ))}
        </div>
      ) : consulta.isError ? (
        <EstadoErro erro={consulta.error} aoTentarDeNovo={() => void consulta.refetch()} />
      ) : (
        <>
          <p className="text-xs text-textoFraco">
            {numero(enderecos)} endereços · {numero(pares)} produto×endereço
            {vazios > 0 ? ` · ${numero(vazios)} vazios` : ''}
            {congelado ? ` · foto de ${dataCurta(e.dataBase)}` : ' · ainda sem foto (congele para ver o saldo congelado)'}
          </p>
          <label className="relative block">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-textoSuave" />
            <input
              value={busca}
              onChange={(ev) => definirBusca(ev.target.value)}
              placeholder="Filtrar por endereço, produto, EAN ou SKU"
              className="min-h-[36px] w-full rounded-lg border border-borda bg-superficie pl-8 pr-3 text-sm focus:border-primaria focus:outline-none"
            />
          </label>
          <div className="max-h-[48vh] overflow-y-auto rounded-lg border border-borda">
            <table className="w-full text-xs">
              <thead className="sticky top-0 z-10 bg-superficie2 text-left text-textoFraco">
                <tr>
                  <th className="px-2.5 py-1.5 font-medium">Endereço</th>
                  <th className="px-2.5 py-1.5 font-medium">Produto</th>
                  <th className="px-2.5 py-1.5 text-right font-medium">Hoje</th>
                  {congelado ? <th className="px-2.5 py-1.5 text-right font-medium">Congelado</th> : null}
                </tr>
              </thead>
              <tbody>
                {[...grupos.entries()].map(([loc, lista]) =>
                  lista.map((i, k) => (
                    <tr key={`${loc}-${i.produto ?? 'v'}`} className={cn(k === 0 && 'border-t border-borda')}>
                      <td className="whitespace-nowrap px-2.5 py-1.5 align-top font-mono">
                        {k === 0 ? (i.endereco ?? loc) : ''}
                      </td>
                      <td className="px-2.5 py-1.5">
                        {i.produto === null ? (
                          <span className="text-textoSuave">endereço vazio</span>
                        ) : (
                          <>
                            <span className="line-clamp-1" title={i.descricao ?? ''}>
                              {i.descricao}
                            </span>
                            <span className="font-mono text-2xs text-textoSuave">
                              {i.produto}
                              {i.ean ? ` · ${i.ean}` : ''}
                            </span>
                          </>
                        )}
                      </td>
                      <td className="tabular px-2.5 py-1.5 text-right align-top">
                        {i.saldoAtual === null ? '' : numero(i.saldoAtual)}
                      </td>
                      {congelado ? (
                        <td className="tabular px-2.5 py-1.5 text-right align-top font-semibold">
                          {i.saldoCongelado === null ? '' : numero(i.saldoCongelado)}
                        </td>
                      ) : null}
                    </tr>
                  )),
                )}
              </tbody>
            </table>
            {filtrados.length === 0 ? (
              <p className="py-4 text-center text-xs text-textoSuave">Nada com esse filtro.</p>
            ) : null}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Busca de escopo: um inventário grande tem dezenas. Digita o número (ou a
 * rua) e as colunas filtram; Enter abre o escopo se sobrar um só, ou se o
 * número digitado for exato.
 */
function BuscaEscopo({
  valor,
  aoMudar,
  total,
  achados,
  aoAbrir,
}: {
  valor: string;
  aoMudar: (v: string) => void;
  total: number;
  achados: number;
  aoAbrir: () => void;
}): JSX.Element {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <label className="relative w-full max-w-xs">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-textoSuave" />
        <input
          value={valor}
          onChange={(ev) => aoMudar(ev.target.value)}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') aoAbrir();
            if (ev.key === 'Escape') aoMudar('');
          }}
          inputMode="search"
          placeholder="Escopo nº ou rua — Enter abre"
          className="min-h-toque w-full rounded-lg border border-borda bg-superficie pl-9 pr-3 text-sm shadow-xs focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12"
        />
      </label>
      {valor ? (
        <span className="text-xs text-textoFraco">
          {numero(achados)} de {numero(total)} escopos
          {achados === 0 ? ' — nenhum com esse número ou rua' : ''}
        </span>
      ) : null}
    </div>
  );
}

const TEXTO_FECHAR: Record<Exclude<EtapaFechamento, 'integracao'>, { titulo: string; pbs: string; contagem: number }> = {
  contagem: { titulo: 'Fechar a 1ª contagem', pbs: 'Fechamento da Contagem', contagem: 0 },
  recontagem: { titulo: 'Fechar a recontagem', pbs: 'Fechamento da Re-contagem', contagem: 1 },
  final: { titulo: 'Fechar a contagem final', pbs: 'Fechamento da Contagem Final', contagem: 2 },
};

/**
 * Fechar a etapa em que o escopo está — o formulário de fechamento do PBS,
 * com a mesma gravação. Antes de confirmar, a tela diz o que vai acontecer
 * com o que ainda não foi bipado: no PBS isso só aparece depois, no
 * resultado (no escopo 2135, 7 de 7 endereços sem bipe viraram ajuste).
 */
function Fechar({
  escopo: e,
  inventario,
  etapa,
}: {
  escopo: EscopoInventario;
  inventario: number;
  etapa: Exclude<EtapaFechamento, 'integracao'>;
}): JSX.Element {
  const fechar = useFecharEtapa(inventario);
  const enderecos = useEnderecosInventario(inventario);
  const previa = usePreviaFechamento(inventario, e.escopo, (TEXTO_FECHAR[etapa].contagem + 1) as 1 | 2 | 3);
  const [confirmando, definirConfirmando] = React.useState(false);
  const [aceitaZerar, definirAceitaZerar] = React.useState(false);
  const zeragem = previa.data?.coletas === 0 && (previa.data?.enderecosSemBipe ?? 0) > 0;
  const [copiarRecontagem, definirCopiarRecontagem] = React.useState(false);
  const textos = TEXTO_FECHAR[etapa];
  const a = andamento(e);
  const faltam = a ? Math.max(0, a.alvo - a.feitos) : 0;
  // O que vai virar falta, separado pelo log do coletor: endereço que nem foi
  // visitado (ainda dá para mandar alguém) e endereço visitado em que ninguém
  // informou produto (a pessoa passou e achou vazio — confirme antes).
  const n = textos.contagem;
  const doEscopo = (enderecos.data?.enderecos ?? []).filter((x) => x.escopo === e.escopo);
  const alvoEnd = doEscopo.filter((x) => (n === 0 ? x.comSaldo === true : x.divergente[n - 1]));
  const naoVisitados = alvoEnd.filter((x) => !x.bipado[n] && !x.visitado[n]).length;
  const visitadosSemProduto = alvoEnd.filter((x) => !x.bipado[n] && x.visitado[n]).length;

  return (
    <div className="rounded-xl border border-primaria/30 bg-primaria-suave p-4">
      <p className="flex items-center gap-2 text-sm font-semibold">
        <Flag className="h-4 w-4" />
        Próximo passo: {textos.titulo.toLowerCase()}
      </p>
      {a ? (
        <div className="mt-3">
          <div className="flex justify-between text-xs">
            <span>Endereços bipados na {a.rotulo}</span>
            <span className="tabular font-semibold">
              {numero(a.feitos)} / {numero(a.alvo)}
            </span>
          </div>
          <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-superficie3">
            <span
              className="gradiente-marca block h-full rounded-full"
              style={{ width: `${a.alvo > 0 ? Math.min(100, (a.feitos / a.alvo) * 100) : 0}%` }}
            />
          </div>
        </div>
      ) : null}
      {faltam > 0 ? (
        <p className="mt-2 flex items-start gap-1.5 rounded-lg bg-status-atencaoFundo px-3 py-2 text-xs">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-status-atencao" />
          {numero(faltam)}{' '}
          {plural(faltam, 'endereço com saldo ainda não foi bipado', 'endereços com saldo ainda não foram bipados')}.
          Ao fechar, o que não foi contado entra como zero — o saldo deles vira divergência (falta). Endereço
          vazio sem bipe não pesa: dá zero contra zero.
          {enderecos.data && (naoVisitados > 0 || visitadosSemProduto > 0) ? (
            <span className="mt-1 block">
              Pelo log do coletor: <b>{numero(naoVisitados)}</b> nem foram visitados
              {visitadosSemProduto > 0 ? (
                <>
                  {' '}
                  e <b>{numero(visitadosSemProduto)}</b> foram visitados sem produto informado — veja no{' '}
                  <Link to={`/inventario/${inventario}/mapa`} className="font-semibold underline">
                    mapa
                  </Link>
                  .
                </>
              ) : (
                '.'
              )}
            </span>
          ) : null}
        </p>
      ) : null}
      {previa.data && previa.data.enderecosSemBipe > 0 ? (
        <>
          <div className="mt-2 grid grid-cols-2 gap-2 text-center sm:grid-cols-4">
            <div className="rounded-lg bg-superficie px-2 py-1.5">
              <p className="text-2xs text-textoFraco">Endereços sem bipe</p>
              <p className="tabular text-sm font-bold">{numero(previa.data.enderecosSemBipe)}</p>
            </div>
            <div className="rounded-lg bg-superficie px-2 py-1.5">
              <p className="text-2xs text-textoFraco">Unidades em falta</p>
              <p className="tabular text-sm font-bold text-status-divergencia">{numero(previa.data.unidadesEmRisco)}</p>
            </div>
            <div className="rounded-lg bg-superficie px-2 py-1.5">
              <p className="text-2xs text-textoFraco">Produtos</p>
              <p className="tabular text-sm font-bold">{numero(previa.data.produtosEmRisco)}</p>
            </div>
            <div className="rounded-lg bg-superficie px-2 py-1.5">
              <p className="text-2xs text-textoFraco" title="Custo da última nota de compra, como na consulta Resultado Inventário Consolidado">
                Valor em falta
              </p>
              <p className="tabular text-sm font-bold text-status-divergencia">{moeda(previa.data.valorEmRisco)}</p>
            </div>
          </div>
          <ListaEmRisco escopo={e.escopo} contagem={previa.data.contagem} itens={previa.data.itens} />
        </>
      ) : null}
      {previa.data ? <DivergenciaAoVivo escopo={e.escopo} previa={previa.data} /> : null}
      {zeragem ? (
        <div className="mt-2 rounded-lg border border-status-divergencia/40 bg-status-divergenciaFundo px-3 py-2 text-xs">
          <p className="font-semibold text-status-divergencia">Contagem sem coletas</p>
          <p className="mt-0.5 text-textoFraco">
            O fechamento considera quantidade zero para todos os endereços do escopo.
          </p>
          <label className="mt-1.5 flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={aceitaZerar}
              onChange={(ev) => definirAceitaZerar(ev.target.checked)}
              className="h-4 w-4"
            />
            Confirmo a zeragem
          </label>
        </div>
      ) : null}
      {etapa === 'final' ? (
        <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs">
          <input
            type="checkbox"
            checked={copiarRecontagem}
            onChange={(ev) => definirCopiarRecontagem(ev.target.checked)}
            className="mt-0.5 h-4 w-4"
          />
          <span>
            <b>Copiar a recontagem</b> como contagem final (campo "Copiar Recontagem" do PBS): ignora a
            3ª contagem e usa o resultado da recontagem.
          </span>
        </label>
      ) : null}
      <Executando ativo={fechar.isPending} oQue="Fechando a contagem" />
      {fechar.isError ? (
        <p className="mt-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-xs text-status-divergencia">
          {mensagemDeErro(fechar.error, 'Não foi possível fechar.')}
        </p>
      ) : null}
      {fechar.isSuccess ? (
        <p className="mt-2 rounded-lg bg-status-livreFundo px-3 py-2 text-xs">
          Fechado (nº {fechar.data.fechamento}): {numero(fechar.data.linhas)} linhas de resultado,{' '}
          {numero(fechar.data.divergentes)} com divergência.
        </p>
      ) : confirmando ? (
        <div className="mt-3 space-y-2">
          <p className="text-xs">
            Grava no ERP o "{textos.pbs}", com a mesma regra do PBS. Depois de fechado, a correção é
            feita pelo PBS.
          </p>
          <div className="flex gap-2">
            <Botao
              variante="primaria"
              tamanho="pequeno"
              carregando={fechar.isPending}
              onClick={() =>
                fechar.mutate({
                  escopo: e.escopo,
                  etapa,
                  ...(etapa === 'final' ? { copiarRecontagem } : {}),
                })
              }
            >
              <Check className="h-4 w-4" />
              Confirmar fechamento
            </Botao>
            <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirConfirmando(false)}>
              Voltar
            </Botao>
          </div>
        </div>
      ) : (
        <Botao
          variante="primaria"
          className="mt-3 w-full"
          disabled={zeragem && !aceitaZerar}
          onClick={() => definirConfirmando(true)}
        >
          <Flag className="h-4 w-4" />
          {textos.titulo}
        </Botao>
      )}
    </div>
  );
}

/**
 * As consultas do menu Estoque > Inventário do PBS, a um clique. A lista vem
 * do próprio menu do PBS; na gaveta do escopo, abrem já filtradas por ele.
 */
function ConsultasDoInventario({
  inventario,
  escopo,
  compacto = false,
}: {
  inventario: number;
  escopo?: number | undefined;
  compacto?: boolean;
}): JSX.Element | null {
  const consultas = useConsultasInventario();
  const lista = consultas.data?.consultas ?? [];
  if (lista.length === 0) return null;
  return (
    <div className={cn(!compacto && 'rounded-xl border border-borda bg-superficie p-3 shadow-xs')}>
      <p className={cn('mb-2 flex items-center gap-1.5 font-semibold', compacto ? 'text-sm' : 'text-[13px]')}>
        <FileSearch className="h-4 w-4 text-textoFraco" />
        Consultas do PBS{escopo ? ` do escopo ${escopo}` : ''}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {lista.map((c) => (
          <Link
            key={c.consulta}
            to={`/inventario/${inventario}/consulta/${encodeURIComponent(c.consulta)}${escopo ? `?escopo=${escopo}` : ''}`}
            className="rounded-lg border border-borda bg-superficie px-2.5 py-1.5 text-xs font-medium hover:border-primaria hover:text-primaria"
          >
            {c.descricao}
          </Link>
        ))}
      </div>
    </div>
  );
}

/**
 * Integração de Estoque — a única etapa que mexe no kardex.
 *
 * Mesma gravação do formulário "Integração de Estoque do Inventário" do PBS:
 * lança em ESTOQUE_LANCAMENTOS (tipo 7, documento = escopo) a diferença entre
 * o saldo da foto e o estoque novo do fechamento final. Antes de confirmar, a
 * tela mostra quanto vai entrar e sair, calculado com a mesma regra.
 */
function Integrar({ escopo: e, inventario }: { escopo: EscopoInventario; inventario: number }): JSX.Element {
  const previa = usePreviaIntegracao(inventario, e.escopo);
  const integrar = useFecharEtapa(inventario);
  const [movimento, definirMovimento] = React.useState(hojeLocal());
  // As 109 integrações desde 2025 foram com 'N'; marcar é escolha explícita.
  const [contabilizar, definirContabilizar] = React.useState(false);
  const [confirmacao, definirConfirmacao] = React.useState('');
  const p = previa.data;

  return (
    <div className="rounded-xl border border-status-atencao/40 bg-status-atencaoFundo p-4">
      <p className="flex items-center gap-2 text-sm font-semibold">
        <Flag className="h-4 w-4" />
        Último passo: integrar ao estoque
      </p>
      <p className="mt-1 text-xs text-textoFraco">
        É aqui que o kardex muda. Mesma gravação do PBS ("Integração de Estoque do Inventário"): entra e
        sai a diferença entre o saldo congelado e o estoque novo do fechamento final.
      </p>

      {previa.isLoading ? (
        <Esqueleto className="mt-3 h-16" />
      ) : p ? (
        <>
          {!p.empresaWms ? (
            <p className="mt-3 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-xs text-status-divergencia">
              A empresa deste escopo não está marcada como WMS no PBS (PARAMETROS_ESTOQUE.EMPRESA_WMS). A
              regra do PBS não lança nada nesse caso.
            </p>
          ) : null}
          <div className="mt-3 grid grid-cols-3 gap-2 text-center">
            <div className="rounded-lg bg-superficie px-2 py-2">
              <p className="text-2xs text-textoFraco">Entradas</p>
              <p className="tabular text-base font-bold text-status-livre">+{numero(p.entradas)}</p>
            </div>
            <div className="rounded-lg bg-superficie px-2 py-2">
              <p className="text-2xs text-textoFraco">Saídas</p>
              <p className="tabular text-base font-bold text-status-divergencia">−{numero(p.saidas)}</p>
            </div>
            <div className="rounded-lg bg-superficie px-2 py-2">
              <p className="text-2xs text-textoFraco">Lançamentos</p>
              <p className="tabular text-base font-bold">{numero(p.linhas)}</p>
              <p className="text-2xs text-textoSuave">{numero(p.produtos)} produtos</p>
            </div>
          </div>
        </>
      ) : null}

      <div className="mt-3 grid grid-cols-2 gap-3">
        <label>
          <span className="rotulo mb-1 block">Data do movimento</span>
          <input
            type="date"
            value={movimento}
            onChange={(ev) => definirMovimento(ev.target.value)}
            className="min-h-toque w-full rounded-lg border border-borda bg-superficie px-3 text-sm"
          />
        </label>
        <label className="flex cursor-pointer items-end gap-2 pb-2 text-sm">
          <input
            type="checkbox"
            checked={contabilizar}
            onChange={(ev) => definirContabilizar(ev.target.checked)}
            className="h-4 w-4"
          />
          Contabilizar
        </label>
      </div>

      <Executando ativo={integrar.isPending} oQue="Integrando ao estoque" />
      {integrar.isError ? (
        <p className="mt-2 rounded-lg bg-status-divergenciaFundo px-3 py-2 text-xs text-status-divergencia">
          {mensagemDeErro(integrar.error, 'Não foi possível integrar.')}
        </p>
      ) : null}

      {integrar.isSuccess ? (
        <p className="mt-3 rounded-lg bg-status-livreFundo px-3 py-2 text-xs">
          Integrado (nº {integrar.data.fechamento}): {numero(integrar.data.lancamentos?.linhas ?? 0)} lançamentos
          no kardex, +{numero(integrar.data.lancamentos?.entradas ?? 0)} / −
          {numero(integrar.data.lancamentos?.saidas ?? 0)}.
        </p>
      ) : (
        <div className="mt-3 space-y-2">
          <label className="block text-xs">
            Para confirmar, digite o número do escopo (<b>{e.escopo}</b>):
            <input
              value={confirmacao}
              onChange={(ev) => definirConfirmacao(ev.target.value.replace(/\D/g, ''))}
              inputMode="numeric"
              className="mt-1 min-h-[38px] w-full rounded-lg border border-borda bg-superficie px-3 text-sm tabular"
            />
          </label>
          <Botao
            variante="primaria"
            className="w-full"
            disabled={confirmacao !== String(e.escopo) || !movimento}
            carregando={integrar.isPending}
            onClick={() =>
              integrar.mutate({ escopo: e.escopo, etapa: 'integracao', movimento, contabilizar })
            }
          >
            <Check className="h-4 w-4" />
            Integrar ao estoque
          </Botao>
        </div>
      )}
    </div>
  );
}

/** A última etapa feita do escopo — a única que dá para desfazer (restritiva do PBS). */
const ULTIMA_FEITA: Partial<
  Record<EtapaEscopo, { etapa: EtapaDesfazer; rotulo: string; acao: string; botao: (escopo: number) => string }>
> = {
  montagem: { etapa: 'escopo', rotulo: 'o escopo', acao: 'Excluir', botao: (n) => `Excluir escopo ${n}` },
  contagem: { etapa: 'congelamento', rotulo: 'o congelamento', acao: 'Descongelar', botao: () => 'Descongelar o estoque' },
  recontagem: {
    etapa: 'contagem',
    rotulo: 'o fechamento da 1ª contagem',
    acao: 'Desfazer',
    botao: () => 'Desfazer fechamento da 1ª contagem',
  },
  final: {
    etapa: 'recontagem',
    rotulo: 'o fechamento da recontagem',
    acao: 'Desfazer',
    botao: () => 'Desfazer fechamento da recontagem',
  },
  integracao: {
    etapa: 'final',
    rotulo: 'o fechamento da contagem final',
    acao: 'Desfazer',
    botao: () => 'Desfazer fechamento da contagem final',
  },
  integrado: {
    etapa: 'integracao',
    rotulo: 'a integração ao estoque (estorno do kardex)',
    acao: 'Estornar',
    botao: () => 'Estornar a integração',
  },
};

/** O que cada desfazer apaga, dito na confirmação. */
const O_QUE_APAGA: Record<EtapaDesfazer, string> = {
  escopo:
    'Apaga o escopo do ERP (endereços e produtos) e o tira deste inventário. Bipes de uma contagem que nunca foi fechada saem junto. O PBS recusa se a contagem já foi fechada.',
  congelamento:
    'Apaga o congelamento (a foto do estoque). O coletor volta a dizer "Congelamento do Escopo não Realizado" e o escopo pode ser congelado de novo. Coletas já enviadas ficam.',
  contagem:
    'Roda a SQL-Restritiva e a SQL-Exclusão do formulário no dicionário do PBS: apaga os resultados da 1ª contagem e a linha do formulário.',
  recontagem:
    'Roda a SQL-Restritiva e a SQL-Exclusão do formulário no dicionário do PBS: apaga os resultados da recontagem e a linha do formulário.',
  final:
    'Roda a SQL-Restritiva e a SQL-Exclusão do formulário no dicionário do PBS: apaga os resultados da contagem final e a linha do formulário.',
  integracao:
    'Roda a SQL-Exclusão do formulário no dicionário do PBS: apaga os lançamentos desta integração no kardex (estorno).',
};

/*
 * As chaves de Fechar, Integrar e Desfazer têm PREFIXO próprio de propósito:
 * com a mesma chave (`escopo-etapa`) nos três irmãos, o React duplicava um
 * painel e sumia com outro ao mudar de etapa — o "próximo passo" repetido e o
 * "Desfazer" que não aparecia depois de fechar a final.
 */

/**
 * Desfazer = o "excluir" do formulário no PBS: roda a SQL-Restritiva (que
 * recusa se a etapa seguinte já existe) e a SQL-Exclusão dele. Na integração,
 * a exclusão apaga os lançamentos do kardex — é o estorno, por isso pede o
 * número do escopo digitado.
 */
function Desfazer({ escopo: e, inventario }: { escopo: EscopoInventario; inventario: number }): JSX.Element | null {
  const desfazer = useDesfazerEtapa(inventario);
  const [aberto, definirAberto] = React.useState(false);
  const [confirmacao, definirConfirmacao] = React.useState('');
  const alvo = ULTIMA_FEITA[e.etapa];
  if (!alvo) return null;
  // Estorno do kardex e exclusão do escopo pedem o número digitado.
  const estorno = alvo.etapa === 'integracao' || alvo.etapa === 'escopo';

  return (
    <div className="border-t border-borda pt-3">
      {!aberto ? (
        <button
          type="button"
          onClick={() => definirAberto(true)}
          className="inline-flex min-h-[36px] items-center gap-1.5 rounded-lg border border-status-divergencia/50 bg-superficie px-3 text-[13px] font-semibold text-status-divergencia transition-colors hover:bg-status-divergenciaFundo"
        >
          {alvo.etapa === 'escopo' ? <Trash2 className="h-4 w-4" /> : <Undo2 className="h-4 w-4" />}
          {alvo.botao(e.escopo)}
        </button>
      ) : (
        <div className="space-y-2 rounded-xl border border-status-divergencia/30 bg-status-divergenciaFundo p-3">
          <p className="text-sm font-semibold">
            {alvo.acao} {alvo.rotulo}?
          </p>
          <p className="text-xs text-textoFraco">
            {O_QUE_APAGA[alvo.etapa]} Só a última etapa feita pode ser desfeita, de trás para frente. Fica no
            histórico do inventário.
          </p>
          {estorno ? (
            <label className="block text-xs">
              Digite o número do escopo (<b>{e.escopo}</b>) para confirmar:
              <input
                value={confirmacao}
                onChange={(ev) => definirConfirmacao(ev.target.value.replace(/\D/g, ''))}
                inputMode="numeric"
                className="mt-1 min-h-[36px] w-full rounded-lg border border-borda bg-superficie px-3 text-sm tabular"
              />
            </label>
          ) : null}
          <Executando ativo={desfazer.isPending} oQue={{ Desfazer: 'Desfazendo', Excluir: 'Excluindo', Descongelar: 'Descongelando', Estornar: 'Estornando' }[alvo.acao] ?? 'Desfazendo'} />
          {desfazer.isError ? (
            <p className="text-xs text-status-divergencia">
              {mensagemDeErro(desfazer.error, 'Não foi possível desfazer.')}
            </p>
          ) : null}
          {desfazer.isSuccess ? (
            <p className="text-xs">Desfeito.</p>
          ) : (
            <div className="flex gap-2">
              <Botao
                variante="perigo"
                tamanho="pequeno"
                disabled={estorno && confirmacao !== String(e.escopo)}
                carregando={desfazer.isPending}
                onClick={() => desfazer.mutate({ escopo: e.escopo, etapa: alvo.etapa })}
              >
                {alvo.acao}
              </Botao>
              <Botao variante="fantasma" tamanho="pequeno" onClick={() => definirAberto(false)}>
                Cancelar
              </Botao>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Horas entre duas datas ISO. */
function horasEntre(a: string, b: string): number {
  return (new Date(b).getTime() - new Date(a).getTime()) / 3_600_000;
}

function formatarHoras(h: number): string {
  if (h < 1) return `${Math.max(1, Math.round(h * 60))} min`;
  if (h < 48) return `${h.toFixed(h < 10 ? 1 : 0).replace('.', ',')} h`;
  return `${Math.round(h / 24)} dias`;
}

/**
 * Quanto a etapa levou (ou está levando), ao lado da mediana dos escopos
 * grandes do CD — tirada dos escopos integrados de 2024–2026.
 */
function TempoDaEtapa({
  marcos,
  indice,
}: {
  marcos: { em: string | null; mediana?: number | undefined }[];
  indice: number;
}): JSX.Element | null {
  const m = marcos[indice];
  const anterior = marcos[indice - 1];
  if (!m || !anterior?.em || m.mediana === undefined) return null;
  const fim = m.em ?? new Date().toISOString();
  const h = horasEntre(anterior.em, fim);
  const lento = h > m.mediana * 2;
  return (
    <p className={cn('text-2xs', lento ? 'text-status-atencao' : 'text-textoSuave')}>
      {m.em ? 'levou' : 'há'} {formatarHoras(h)} · mediana {formatarHoras(m.mediana)}
    </p>
  );
}

/**
 * Coletas registradas depois do fechamento da própria contagem: nenhum
 * fechamento as considera. No PBS isso passa sem aviso — foram 205 nos
 * escopos antigos, 77 só no 2127.
 */
function AvisoPerdidas({ escopo: e }: { escopo: EscopoInventario }): JSX.Element | null {
  const total = e.perdidas[0] + e.perdidas[1] + e.perdidas[2];
  if (total === 0) return null;
  const partes = (['1ª', 'recontagem', 'final'] as const)
    .map((r, i) => (e.perdidas[i] ? `${numero(e.perdidas[i])} na ${r}` : null))
    .filter(Boolean);
  return (
    <div className="flex items-start gap-2 rounded-lg border border-status-atencao/30 bg-status-atencaoFundo px-3 py-2 text-xs">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-status-atencao" />
      <p>
        <b>{numero(total)} coletas perdidas</b> ({partes.join(', ')}): foram bipadas depois de a própria contagem
        já estar fechada, e nenhum fechamento vai considerá-las. Confira com quem estava no coletor.
      </p>
    </div>
  );
}

const COLUNAS_RISCO_CSV: ColunaCsv<ItemEmRisco>[] = [
  { cabecalho: 'Prioridade', valor: (i) => (i.nuncaBipado ? 'Nunca bipado' : 'Já visitado em outra contagem') },
  { cabecalho: 'Endereço', valor: (i) => i.endereco ?? '', texto: true },
  { cabecalho: 'Localizador', valor: (i) => i.localizador, texto: true },
  { cabecalho: 'SKU', valor: (i) => i.produto, texto: true },
  { cabecalho: 'Descrição', valor: (i) => i.descricao ?? '' },
  { cabecalho: 'Unidades (foto)', valor: (i) => i.unidades },
  { cabecalho: 'Custo', valor: (i) => i.custo.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) },
  { cabecalho: 'Valor em falta', valor: (i) => i.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) },
];

/**
 * O que ir conferir antes de fechar — a "recontagem dirigida". Primeiro o que
 * nunca foi bipado em contagem nenhuma, depois pelo maior valor. O Excel sai
 * na mesma ordem, para imprimir e entregar a quem vai ao corredor.
 */
function ListaEmRisco({
  escopo,
  contagem,
  itens,
}: {
  escopo: number;
  contagem: 1 | 2 | 3;
  itens: ItemEmRisco[];
}): JSX.Element {
  const [aberta, definirAberta] = React.useState(false);
  const nunca = itens.filter((i) => i.nuncaBipado).length;
  return (
    <div className="mt-2 rounded-lg border border-borda bg-superficie">
      <div className="flex items-center gap-2 px-3 py-2">
        <button
          type="button"
          onClick={() => definirAberta((v) => !v)}
          className="flex-1 text-left text-xs font-semibold"
        >
          {aberta ? 'Esconder' : 'Ver'} o que conferir antes de fechar
          {nunca > 0 ? <span className="ml-1 font-normal text-status-atencao">· {numero(nunca)} nunca bipados</span> : null}
        </button>
        <Botao
          variante="fantasma"
          tamanho="micro"
          onClick={() =>
            baixarCsv(`conferir-escopo-${escopo}-contagem-${contagem}-${carimboArquivo()}`, itens, COLUNAS_RISCO_CSV)
          }
        >
          <Download className="h-3.5 w-3.5" />
          Excel
        </Botao>
      </div>
      {aberta ? (
        <div className="max-h-64 overflow-y-auto border-t border-borda">
          <table className="w-full text-xs">
            <tbody>
              {itens.map((i) => (
                <tr key={`${i.localizador}-${i.produto}`} className="border-b border-borda last:border-0">
                  <td className="whitespace-nowrap px-2.5 py-1.5 align-top font-mono">
                    {i.endereco ?? i.localizador}
                    {i.nuncaBipado ? (
                      <span className="ml-1 rounded bg-status-atencaoFundo px-1 font-sans text-2xs font-bold text-status-atencao">
                        nunca bipado
                      </span>
                    ) : null}
                  </td>
                  <td className="px-2.5 py-1.5">
                    <span className="line-clamp-1" title={i.descricao ?? ''}>
                      {i.descricao}
                    </span>
                    <span className="font-mono text-2xs text-textoSuave">{i.produto}</span>
                  </td>
                  <td className="tabular whitespace-nowrap px-2.5 py-1.5 text-right align-top">{numero(i.unidades)} un</td>
                  <td className="tabular whitespace-nowrap px-2.5 py-1.5 text-right align-top font-semibold">
                    {moeda(i.valor)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

const COLUNAS_DIVERGENCIA_CSV: ColunaCsv<ItemDivergente>[] = [
  { cabecalho: 'Endereço', valor: (i) => i.endereco ?? '', texto: true },
  { cabecalho: 'Localizador', valor: (i) => i.localizador, texto: true },
  { cabecalho: 'SKU', valor: (i) => i.produto, texto: true },
  { cabecalho: 'Descrição', valor: (i) => i.descricao ?? '' },
  { cabecalho: 'Foto', valor: (i) => i.foto },
  { cabecalho: 'Contado', valor: (i) => i.contado },
  { cabecalho: 'Diferença', valor: (i) => i.diferenca },
  { cabecalho: 'Custo', valor: (i) => i.custo.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) },
  { cabecalho: 'Valor', valor: (i) => i.valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 }) },
];

const COLUNAS_CODIGOS_CSV: ColunaCsv<CodigoSemCadastro>[] = [
  { cabecalho: 'Código lido', valor: (c) => c.codigo, texto: true },
  { cabecalho: 'Endereço', valor: (c) => c.endereco ?? '', texto: true },
  { cabecalho: 'Localizador', valor: (c) => c.localizador ?? '', texto: true },
  { cabecalho: 'Leituras', valor: (c) => c.leituras },
];

/**
 * O que a contagem em curso já mostra, antes de fechar: a divergência nos
 * endereços já bipados (contado × foto) e os códigos que o coletor recusou
 * por não achar o produto — esses não entram na contagem, e o resultado do
 * PBS não avisa.
 */
function DivergenciaAoVivo({ escopo, previa }: { escopo: number; previa: PreviaFechamento }): JSX.Element | null {
  const [aberta, definirAberta] = React.useState<'div' | 'cod' | null>(null);
  const d = previa.divergencias;
  const c = previa.codigosSemCadastro;
  if (d.length === 0 && c.length === 0) return null;
  const sobra = d.filter((i) => i.diferenca > 0).reduce((s, i) => s + i.diferenca, 0);
  const falta = d.filter((i) => i.diferenca < 0).reduce((s, i) => s + i.diferenca, 0);
  const valor = d.reduce((s, i) => s + i.valor, 0);
  return (
    <div className="mt-2 rounded-lg border border-borda bg-superficie">
      {d.length > 0 ? (
        <>
          <div className="flex items-center gap-2 px-3 py-2 text-xs">
            <button
              type="button"
              onClick={() => definirAberta((v) => (v === 'div' ? null : 'div'))}
              className="flex-1 text-left"
            >
              <span className="font-semibold">Divergência até agora</span>{' '}
              <span className="text-textoFraco">nos endereços já bipados:</span> <b>{numero(d.length)}</b>{' '}
              {plural(d.length, 'item', 'itens')} · <span className="text-status-livre">+{numero(sobra)}</span> /{' '}
              <span className="text-status-divergencia">{numero(falta)}</span> un ·{' '}
              <b className={valor < 0 ? 'text-status-divergencia' : 'text-status-livre'}>{moeda(valor)}</b>
            </button>
            <Botao
              variante="fantasma"
              tamanho="micro"
              onClick={() =>
                baixarCsv(
                  `divergencia-escopo-${escopo}-contagem-${previa.contagem}-${carimboArquivo()}`,
                  d,
                  COLUNAS_DIVERGENCIA_CSV,
                )
              }
            >
              <Download className="h-3.5 w-3.5" />
              Excel
            </Botao>
          </div>
          {aberta === 'div' ? (
            <div className="max-h-64 overflow-y-auto border-t border-borda">
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-superficie2 text-left text-textoFraco">
                  <tr>
                    <th className="px-2.5 py-1.5 font-medium">Endereço</th>
                    <th className="px-2.5 py-1.5 font-medium">Produto</th>
                    <th className="px-2.5 py-1.5 text-right font-medium">Foto</th>
                    <th className="px-2.5 py-1.5 text-right font-medium">Contado</th>
                    <th className="px-2.5 py-1.5 text-right font-medium">Valor</th>
                  </tr>
                </thead>
                <tbody>
                  {d.map((i) => (
                    <tr key={`${i.localizador}-${i.produto}`} className="border-t border-borda">
                      <td className="whitespace-nowrap px-2.5 py-1.5 font-mono">{i.endereco ?? i.localizador}</td>
                      <td className="px-2.5 py-1.5">
                        <span className="line-clamp-1" title={i.descricao ?? ''}>
                          {i.descricao}
                        </span>
                        <span className="font-mono text-2xs text-textoSuave">{i.produto}</span>
                      </td>
                      <td className="tabular px-2.5 py-1.5 text-right">{numero(i.foto)}</td>
                      <td
                        className={cn(
                          'tabular px-2.5 py-1.5 text-right font-semibold',
                          i.diferenca > 0 ? 'text-status-livre' : 'text-status-divergencia',
                        )}
                      >
                        {numero(i.contado)}
                      </td>
                      <td className="tabular whitespace-nowrap px-2.5 py-1.5 text-right">{moeda(i.valor)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </>
      ) : null}
      {c.length > 0 ? (
        <>
          <div className={cn('flex items-center gap-2 px-3 py-2 text-xs', d.length > 0 && 'border-t border-borda')}>
            <button
              type="button"
              onClick={() => definirAberta((v) => (v === 'cod' ? null : 'cod'))}
              className="flex-1 text-left"
            >
              <AlertTriangle className="mr-1 inline h-3.5 w-3.5 text-status-atencao" />
              <b>{numero(c.length)}</b> {plural(c.length, 'código lido sem cadastro', 'códigos lidos sem cadastro')}{' '}
              <span className="text-textoFraco">— o coletor recusou e eles não entram na contagem</span>
            </button>
            <Botao
              variante="fantasma"
              tamanho="micro"
              onClick={() =>
                baixarCsv(
                  `codigos-sem-cadastro-escopo-${escopo}-contagem-${previa.contagem}-${carimboArquivo()}`,
                  c,
                  COLUNAS_CODIGOS_CSV,
                )
              }
            >
              <Download className="h-3.5 w-3.5" />
              Excel
            </Botao>
          </div>
          {aberta === 'cod' ? (
            <ul className="max-h-48 overflow-y-auto border-t border-borda px-3 py-1.5 text-xs">
              {c.map((x) => (
                <li key={`${x.codigo}-${x.localizador ?? ''}`} className="flex justify-between gap-2 py-0.5">
                  <span className="truncate font-mono">{x.codigo}</span>
                  <span className="shrink-0 text-textoFraco">
                    {x.endereco ?? x.localizador ?? '—'} · {numero(x.leituras)}×
                  </span>
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </div>
  );
}

/**
 * Erro de rede ou do balanceador (sem resposta, 502/503/504): a regra do PBS
 * pode ter terminado no ERP mesmo assim. A tela já recarrega o estado — o
 * texto diz para conferir antes de repetir, em vez de convidar a clicar de
 * novo no escuro.
 */
function mensagemDeErro(erro: unknown, padrao: string): string {
  if (erro instanceof ErroHttp) {
    if (erro.status === 0 || erro.status === 502 || erro.status === 503 || erro.status === 504) {
      return 'A conexão caiu antes da resposta, mas a operação pode ter sido concluída no ERP. A tela foi atualizada — confira a etapa do escopo antes de repetir.';
    }
    return erro.message;
  }
  return padrao;
}

/** As etapas em que reinserir produtos é aceito (ver api/inventario/produtos.ts). */
const ETAPAS_REINSERIR: EtapaEscopo[] = ['montagem', 'contagem'];

/**
 * Reinserir produtos: o botão "Insere Produtos Localizador" do PBS, apertado
 * de novo em todos os escopos do inventário de uma vez.
 *
 * É o que o CD faz quando congela num dia e conta no outro: o produto que
 * chegou ao endereço depois da montagem passa a fazer parte do escopo, e o
 * coletor aceita a bipagem dele. Vale para os escopos em montagem ou 1ª
 * contagem; os que já fecharam a 1ª ficam de fora (a recontagem trabalha
 * sobre o que divergiu).
 */
function ReinserirProdutos({
  inventario,
  escopos,
  aoFechar,
}: {
  inventario: number;
  escopos: EscopoInventario[];
  aoFechar: () => void;
}): JSX.Element {
  const reinserir = useReinserirProdutos(inventario);
  const aceitam = escopos.filter((e) => ETAPAS_REINSERIR.includes(e.etapa));
  const pulados = escopos.length - aceitam.length;
  const r = reinserir.data;
  const ok = r ? r.escopos.filter((e) => !e.erro) : [];
  const entraram = ok.reduce((s, e) => s + e.entraram, 0);
  const sairam = ok.reduce((s, e) => s + e.sairam, 0);

  return (
    <Cartao className="space-y-3 p-4">
      <div className="flex items-start gap-3">
        <PackagePlus className="mt-0.5 h-5 w-5 shrink-0 text-primaria" />
        <div className="min-w-0 flex-1 text-sm">
          <p className="font-semibold">Reinserir produtos nos escopos</p>
          <p className="mt-0.5 text-textoFraco">
            Atualiza os produtos de cada escopo com o que está nos endereços agora. Use quando o congelamento foi
            feito antes do dia da contagem: assim o produto que chegou depois também pode ser bipado. O
            congelamento não muda, e nenhum produto congelado sai do escopo.
          </p>
        </div>
        <button type="button" onClick={aoFechar} className="text-textoFraco hover:text-texto" aria-label="Fechar">
          <X className="h-4 w-4" />
        </button>
      </div>

      {!r ? (
        <div className="flex flex-wrap items-center gap-2 border-t border-borda pt-3">
          <span className="text-[12px] text-textoFraco">
            {numero(aceitam.length)} {plural(aceitam.length, 'escopo será atualizado', 'escopos serão atualizados')}
            {pulados > 0 ? ` · ${numero(pulados)} já passaram da 1ª contagem e ficam como estão` : ''}.
          </span>
          <Botao
            className="ml-auto"
            variante="primaria"
            tamanho="pequeno"
            carregando={reinserir.isPending}
            disabled={aceitam.length === 0}
            onClick={() => reinserir.mutate()}
          >
            Reinserir em {numero(aceitam.length)} {plural(aceitam.length, 'escopo', 'escopos')}
          </Botao>
        </div>
      ) : (
        <div className="space-y-2 border-t border-borda pt-3 text-[12.5px]">
          <p className="font-semibold">
            {numero(ok.length)} {plural(ok.length, 'escopo atualizado', 'escopos atualizados')}:{' '}
            {numero(entraram)} {plural(entraram, 'produto entrou', 'produtos entraram')}
            {sairam > 0 ? `, ${numero(sairam)} ${plural(sairam, 'saiu', 'saíram')}` : ''}.
          </p>
          <ul className="grid gap-1 sm:grid-cols-2 xl:grid-cols-3">
            {r.escopos.map((e) => (
              <li
                key={e.escopo}
                className={cn(
                  'rounded-lg px-2.5 py-1.5',
                  e.erro ? 'bg-status-divergenciaFundo text-status-divergencia' : 'bg-superficie2',
                )}
                title={
                  e.amostraEntraram.length > 0
                    ? `Entraram: ${e.amostraEntraram.join(', ')}${e.entraram > e.amostraEntraram.length ? '…' : ''}`
                    : undefined
                }
              >
                <b className="tabular">Escopo {e.escopo}</b>{' '}
                {e.erro ? (
                  <span>— não atualizou: {e.erro}</span>
                ) : (
                  <span className="tabular text-textoFraco">
                    {numero(e.depois)} produtos
                    {e.entraram > 0 ? <b className="text-status-livre"> · +{numero(e.entraram)}</b> : null}
                    {e.sairam > 0 ? <b className="text-status-atencao"> · −{numero(e.sairam)}</b> : null}
                    {e.mantidos > 0 ? ` · ${numero(e.mantidos)} mantidos (congelados)` : null}
                    {e.entraram === 0 && e.sairam === 0 ? ' · sem mudança' : null}
                  </span>
                )}
              </li>
            ))}
          </ul>
          {sairam > 0 ? (
            <p className="text-[12px] text-textoFraco">
              Saiu = produto que não está mais no endereço e não estava congelado.
            </p>
          ) : null}
        </div>
      )}
      {reinserir.isError ? (
        <p className="text-sm text-status-divergencia">
          {mensagemDeErro(reinserir.error, 'Não foi possível reinserir os produtos.')}
        </p>
      ) : null}
    </Cartao>
  );
}

/**
 * Inventário cíclico: gerar mais escopos — a próxima rua, os SKUs da semana.
 * O critério é o do inventário; o resto é o mesmo gerador da criação.
 */
function GerarMaisEscopos({
  inventario,
  centro,
  criterio,
  aoFechar,
}: {
  inventario: number;
  centro: number;
  criterio: CriterioCiclico;
  aoFechar: () => void;
}): JSX.Element {
  const gerar = useGerarCiclico();
  const [selecao, definirSelecao] = React.useState<{ s: SelecaoCiclica | null; grupos: number }>({
    s: null,
    grupos: 0,
  });
  const [chave, definirChave] = React.useState(0);
  const r = gerar.data;
  return (
    <Cartao className="space-y-3 p-4">
      <div className="flex items-center gap-2">
        <RefreshCcw className="h-4 w-4 text-primaria" />
        <p className="text-[14px] font-semibold">
          Gerar escopos {criterio === 'SKU' ? 'pelos SKUs' : 'por rua'}
        </p>
        <Botao variante="fantasma" tamanho="micro" className="ml-auto" onClick={aoFechar} aria-label="Fechar">
          <X className="h-3.5 w-3.5" />
        </Botao>
      </div>
      <GeradorCiclico
        key={chave}
        centro={centro}
        criterio={criterio}
        aoMudar={(s, grupos) => definirSelecao({ s, grupos })}
      />
      {r ? (
        <div className="rounded-lg border border-status-livre/30 bg-status-livreFundo px-3 py-2 text-[12.5px]">
          <p className="font-semibold">
            {numero(r.gerados.length)} {plural(r.gerados.length, 'escopo gerado', 'escopos gerados')}:{' '}
            {r.gerados.map((g) => `#${g.escopo} (${g.rotulo}, ${numero(g.localizadores)} end.)`).join(' · ') || '—'}
          </p>
          {r.pulados.length > 0 ? (
            <p className="text-status-atencao">
              Ficaram de fora: {r.pulados.map((p) => `${p.rotulo} — ${p.motivo}`).join(' · ')}
            </p>
          ) : null}
        </div>
      ) : null}
      {gerar.isError ? (
        <p className="text-sm text-status-divergencia">
          {gerar.error instanceof Error ? gerar.error.message : 'Não foi possível gerar os escopos.'}
        </p>
      ) : null}
      <div className="flex justify-end">
        <Botao
          variante="primaria"
          tamanho="pequeno"
          disabled={!selecao.s || selecao.grupos === 0}
          carregando={gerar.isPending}
          onClick={() => {
            if (!selecao.s) return;
            gerar.mutate(
              { inventario, ...selecao.s },
              { onSuccess: () => definirChave((k) => k + 1) },
            );
          }}
        >
          {selecao.grupos > 0
            ? `Gerar ${numero(selecao.grupos)} ${selecao.grupos === 1 ? 'escopo' : 'escopos'}`
            : 'Gerar escopos'}
        </Botao>
      </div>
    </Cartao>
  );
}
